import React, { useCallback, useEffect, useState } from 'react';
import { LayoutTemplate, Check, Save, Trash2, Users, Plus } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';

const err = (e) => toast({ title: 'Error', description: e?.response?.data?.detail || 'Failed', variant: 'destructive' });

const SaveLayoutDialog = ({ open, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  useEffect(() => { if (open) { setName(''); setShared(false); } }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="save-layout-dialog">
        <DialogHeader><DialogTitle>Save current layout</DialogTitle></DialogHeader>
        <p className="text-xs text-gray-500">Saves the card order, hidden cards and compact mode.</p>
        <Input autoFocus placeholder="e.g. Month-end payroll" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} data-testid="layout-name-input" />
        <label className="flex items-center gap-2 text-sm"><Checkbox checked={shared} onCheckedChange={(v) => setShared(!!v)} data-testid="layout-share-checkbox" />Share with all HR users</label>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!name.trim()} onClick={() => onSave(name.trim(), shared)} data-testid="layout-save-confirm">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const LayoutsMenu = ({ current, activeId, onApply }) => {
  const [layouts, setLayouts] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const load = useCallback(() => api.get('/api/dashboard/layouts').then((r) => setLayouts(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const create = async (name, shared) => {
    try { const { data } = await api.post('/api/dashboard/layouts', { name, shared, ...current }); setSaveOpen(false); await load(); onApply(data, true); toast({ title: 'Layout saved', description: shared ? `"${name}" is shared with all HR users.` : `"${name}" saved.` }); } catch (e) { err(e); }
  };
  const overwrite = async (l) => {
    try { await api.put(`/api/dashboard/layouts/${l.id}`, { name: l.name, shared: l.shared, ...current }); load(); toast({ title: 'Layout updated', description: `"${l.name}" now matches your dashboard.` }); } catch (e) { err(e); }
  };
  const remove = async (l) => {
    if (!window.confirm(`Delete layout "${l.name}"?`)) return;
    try { await api.delete(`/api/dashboard/layouts/${l.id}`); load(); } catch (e) { err(e); }
  };
  const mine = layouts.filter((l) => l.is_owner);
  const shared = layouts.filter((l) => !l.is_owner);
  const active = layouts.find((l) => l.id === activeId);
  const modified = active && (JSON.stringify(active.widget_order || []) !== JSON.stringify(current.widget_order)
    || JSON.stringify([...(active.hidden_widgets || [])].sort()) !== JSON.stringify([...current.hidden_widgets].sort())
    || !!active.compact !== current.compact);

  const Row = ({ l }) => (
    <DropdownMenuItem onSelect={() => onApply(l)} className="flex items-center gap-2" data-testid={`layout-item-${l.id}`}>
      <Check className={`h-3.5 w-3.5 ${l.id === activeId ? 'text-violet-600' : 'opacity-0'}`} />
      <span className="flex-1 truncate">{l.name}</span>
      {l.shared && l.is_owner && <Users className="h-3 w-3 text-gray-400" title="Shared" />}
      {!l.is_owner && <span className="text-[10px] text-gray-400">{l.owner_name}</span>}
      {l.is_owner && <>
        <button type="button" title="Update with current dashboard" onClick={(e) => { e.stopPropagation(); e.preventDefault(); overwrite(l); }} className="p-1 rounded hover:bg-violet-100" data-testid={`layout-update-${l.id}`}><Save className="h-3 w-3 text-gray-500" /></button>
        <button type="button" title="Delete" onClick={(e) => { e.stopPropagation(); e.preventDefault(); remove(l); }} className="p-1 rounded hover:bg-red-100" data-testid={`layout-delete-${l.id}`}><Trash2 className="h-3 w-3 text-red-500" /></button>
      </>}
    </DropdownMenuItem>
  );

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 hover:border-violet-300 transition-colors" data-testid="layouts-menu-btn">
            <LayoutTemplate className="h-4 w-4 text-violet-600" />
            <span className="max-w-[140px] truncate" data-testid="active-layout-name">{active ? active.name : 'Layouts'}</span>
            {modified && <span className="text-[10px] text-amber-600" title="Dashboard differs from this saved layout — use the save icon to update it" data-testid="layout-modified-hint">• modified</span>}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72" data-testid="layouts-menu">
          <DropdownMenuLabel className="text-xs text-gray-400">My layouts</DropdownMenuLabel>
          {mine.length ? mine.map((l) => <Row key={l.id} l={l} />) : <p className="px-2 py-1 text-xs text-gray-400">None saved yet</p>}
          {shared.length > 0 && <><DropdownMenuSeparator /><DropdownMenuLabel className="text-xs text-gray-400">Shared by HR</DropdownMenuLabel>{shared.map((l) => <Row key={l.id} l={l} />)}</>}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setSaveOpen(true)} data-testid="layout-save-new"><Plus className="h-3.5 w-3.5 mr-2" />Save current as new…</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <SaveLayoutDialog open={saveOpen} onClose={() => setSaveOpen(false)} onSave={create} />
    </>
  );
};
